import { xssColors } from '@/Common/constants';

/** Everything the chooser offers. Signatures and Forecast are their own components (later phases). */
export type ChartGroup = 'XPMC' | 'Stress' | 'Signatures' | 'Forecast';

export const chartGroups: { value: ChartGroup; label: string }[] = [
  { value: 'XPMC', label: 'XPMC' },
  { value: 'Stress', label: 'Stress' },
  { value: 'Signatures', label: 'Fitness Signatures' },
  { value: 'Forecast', label: 'Forecast' },
];

/** The groups ProgressionChart itself draws. */
export type HistoryGroup = 'XPMC' | 'Stress';

export type AxisCfg = {
  title: string;
  /** What sits above the axis: short enough to fit over its labels. */
  short: string;
  side: 'left' | 'right';
  /** Labels and title take the colour of the series the axis mainly serves, so it reads as their scale. */
  color: string;
  /** Lower bound; loads and energies can't go below zero, form and focus can. */
  min?: number;
  /** Head-room above the data, as a fraction of its range: keeps stacked columns from filling the plot. */
  extraMax?: number;
  /**
   * For stacked columns: the axis tops out at the median (non-empty) column plus this fraction of it, or at the
   * tallest column if that is higher, so one huge day doesn't squash all the others.
   */
  medianHeadroom?: number;
  /** Put the top of the data exactly at the top of the plot: no headroom, and no rounding up to a tidy number. */
  exactMax?: boolean;
};

export const AXES: Record<string, AxisCfg> = {
  load: { short: 'TL/RL', title: 'Training / Recovery Load', side: 'left', color: '#1F2937', min: 0, exactMax: true },
  form: { short: 'Form', title: 'Form', side: 'left', color: '#6B7280' },
  power: { short: 'TP', title: 'Threshold Power (W)', side: 'right', color: '#B8860B' },
  power_wkg: { short: 'TP', title: 'Threshold Power (W/kg)', side: 'right', color: '#B8860B' },
  xss: { short: 'XSS', title: 'XSS', side: 'right', color: xssColors.xlss, min: 0, medianHeadroom: 1.2 },
  stress: { short: 'Stress', title: 'Daily Stress (kJ)', side: 'left', color: '#1F2937', min: 0 },
  focus: { short: 'Focus', title: 'Focus (min)', side: 'right', color: '#458141', min: 0 },
  weight: { short: 'Wt', title: 'Weight (kg)', side: 'right', color: '#8B5CF6' },
};

export type SeriesCfg = {
  label: string;
  /** Field of a day's row. */
  field: string;
  type: 'line' | 'column';
  axis: keyof typeof AXES;
  color: string;
  width?: number;
  dash?: number;
  /** Columns stacked on each other (the XSS split) rather than drawn over each other. */
  stacked?: boolean;
  /**
   * A series that is small next to the others on its axis. It is drawn scaled up to fit, and the
   * axis labels carry its own scale in its own colour, so it shares the axis rather than adding one.
   */
  rescale?: boolean;
  /** Colour for its numbers on the axis, where the series colour is too light to read as text. */
  labelColor?: string;
  /** Divide the raw value by this before showing it (HIE arrives in joules). */
  scale?: number;
  decimals: number;
  unit?: string;
  /** Present, but switched off until the athlete turns it on. */
  hidden?: boolean;
};

const kJ = { decimals: 1, unit: 'kJ' };

export const SERIES: Record<string, SeriesCfg> = {
  // Low / High / Peak share one colour scheme everywhere -- the same one the forecast chart and the XSS split use.
  xlss: { label: 'Low XSS', field: 'xlss', type: 'column', axis: 'xss', color: xssColors.xlss, stacked: true, decimals: 0 },
  xhss: { label: 'High XSS', field: 'xhss', type: 'column', axis: 'xss', color: xssColors.xhss, stacked: true, decimals: 0 },
  xpss: { label: 'Peak XSS', field: 'xpss', type: 'column', axis: 'xss', color: xssColors.xpss, stacked: true, decimals: 0 },
  // Only while a filter is on: what the activities that were filtered out contributed, in grey.
  xout: { label: 'Filtered out', field: 'xout', type: 'column', axis: 'xss', color: '#CBD5E1', stacked: true, decimals: 0 },
  ctl: { label: 'Training Load', field: 'ctl', type: 'line', axis: 'load', color: '#111827', width: 3, decimals: 1 },
  atl: { label: 'Recovery Load', field: 'atl', type: 'line', axis: 'load', color: '#26B897', width: 2, decimals: 1, hidden: true },
  ltl: { label: 'Low TL', field: 'ltl', type: 'line', axis: 'load', color: xssColors.xlss, width: 2, dash: 4, decimals: 1, rescale: true, hidden: true },
  htl: { label: 'High TL', field: 'htl', type: 'line', axis: 'load', color: xssColors.xhss, labelColor: '#B58900', width: 2, dash: 4, decimals: 1, rescale: true, hidden: true },
  ptl: { label: 'Peak TL', field: 'ptl', type: 'line', axis: 'load', color: xssColors.xpss, width: 2, dash: 4, decimals: 1, rescale: true, hidden: true },
  tsb: { label: 'Form', field: 'tsb', type: 'line', axis: 'form', color: '#6B7280', width: 2, decimals: 1 },
  ftp: { label: 'Threshold Power', field: 'ftp', type: 'line', axis: 'power', color: '#DAA520', width: 4, dash: 8, decimals: 0, unit: 'W' },
  // Alternate unit for the threshold power line; ftp and ftp_wkg are mutually exclusive, cycled by one "Threshold"
  // chip (see thresholdState/toggleThreshold in ProgressionChart.svelte), not shown as separate chips.
  ftp_wkg: { label: 'Threshold Power (W/kg)', field: 'ftp_wkg', type: 'line', axis: 'power_wkg', color: '#DAA520', width: 4, dash: 8, decimals: 1, unit: 'W/kg', hidden: true },
  weight: { label: 'Weight', field: 'weight', type: 'line', axis: 'weight', color: '#8B5CF6', width: 2, decimals: 1, unit: 'kg', hidden: true },
  // Stacked Low (bottom) -> High -> Peak, in GROUP_SERIES.Stress order, the same way the XSS split stacks.
  liw: { label: 'Low Stress', field: 'liw_daily', type: 'column', axis: 'stress', color: xssColors.xlss, stacked: true, ...kJ },
  hiw: { label: 'High Stress', field: 'hiw_daily', type: 'column', axis: 'stress', color: xssColors.xhss, stacked: true, ...kJ },
  piw: { label: 'Peak Stress', field: 'piw_daily', type: 'column', axis: 'stress', color: xssColors.xpss, stacked: true, ...kJ },
  focus_strain: { label: 'Focus', field: 'focus_strain', type: 'line', axis: 'focus', color: '#458141', width: 3, dash: 4, decimals: 1, unit: 'min' },
};

/** Series of each group, in the order they are drawn (columns first, so lines sit on top of them). */
export const GROUP_SERIES: Record<HistoryGroup, string[]> = {
  XPMC: ['xlss', 'xhss', 'xpss', 'xout', 'ctl', 'atl', 'ltl', 'htl', 'ptl', 'tsb', 'ftp', 'ftp_wkg', 'weight'],
  Stress: ['liw', 'hiw', 'piw'],
};

/** Fields whose value for a day is what was done that day, so a day with several activities adds them up. */
export const SUMMED_FIELDS = ['xss', 'xlss', 'xhss', 'xpss', 'liw_daily', 'hiw_daily', 'piw_daily'];

/** Breakthrough markers are on the chart in every group, but only the XPMC one starts with them showing. */
export const BREAKTHROUGH_COLOR = '#00C853';
export const BREAKTHROUGHS_VISIBLE_IN: HistoryGroup[] = ['XPMC'];

// -- Fitness Signatures: one chart per signature value, stacked and sharing a time axis ----------

export type SignatureLine = {
  id: string;
  label: string;
  /** Field of a day's row (HIE is derived from `atc` in kJ as `hie_kj`). */
  field: string;
  color: string;
  width: number;
  dash?: number;
  decimals: number;
  unit: string;
};

export type SignaturePanel = { id: string; title: string; lines: SignatureLine[] };

/** Threshold, high-intensity energy and peak power take the same low/high/peak colours as everywhere else. */
export const SIGNATURE_PANELS: SignaturePanel[] = [
  {
    id: 'tp',
    title: 'Threshold Power (W)',
    lines: [
      { id: 'ftp', label: 'Threshold Power', field: 'ftp', color: xssColors.xlss, width: 3, decimals: 0, unit: 'W' },
      { id: 'ltp', label: 'Lower Threshold', field: 'ltp', color: '#6D9ED7', width: 2, dash: 4, decimals: 0, unit: 'W' },
    ],
  },
  {
    id: 'hie',
    title: 'High Intensity Energy (kJ)',
    lines: [{ id: 'hie', label: 'HIE', field: 'hie_kj', color: xssColors.xhss, width: 3, decimals: 1, unit: 'kJ' }],
  },
  {
    id: 'pp',
    title: 'Peak Power (W)',
    lines: [{ id: 'pp', label: 'Peak Power', field: 'pp', color: xssColors.xpss, width: 3, decimals: 0, unit: 'W' }],
  },
];

/** Drawn on every signature chart, each on a scale of its own down the right-hand side. */
export type SignatureOverlay = {
  id: string;
  label: string;
  field: string;
  color: string;
  short: string;
  decimals: number;
  unit: string;
  max?: number;
  /** Overlays with the same `axis` share one right-hand scale. */
  axis: string;
  /** Lower bound of that scale; form goes below zero, so it has none. */
  axisMin?: number;
  /** The line takes each day's freshness colour (the form line). */
  styled?: boolean;
  dash?: number;
  /**
   * Set for training load, recovery load and form: each signature chart draws the part of them that goes with it
   * (low for threshold power, high for HIE, peak for peak power), under this one legend entry.
   */
  perPanel?: 'tl' | 'rl' | 'form';
};

/** The fields of the low / high / peak part of load, recovery and form that go with each signature chart. */
export const SIGNATURE_LOAD_FIELDS: Record<string, { label: string; tl: string; rl: string; form: string }> = {
  tp: { label: 'Low', tl: 'ltl', rl: 'lrl', form: 'lf' },
  hie: { label: 'High', tl: 'htl', rl: 'hrl', form: 'hf' },
  pp: { label: 'Peak', tl: 'ptl', rl: 'prl', form: 'pf' },
};

export const SIGNATURE_OVERLAYS: SignatureOverlay[] = [
  { id: 'ctl', label: 'Training Load', field: 'ctl', color: '#111827', short: 'TL', decimals: 1, unit: '', axis: 'load', axisMin: 0, perPanel: 'tl' },
  { id: 'atl', label: 'Recovery Load', field: 'atl', color: '#26B897', short: 'RL', decimals: 1, unit: '', axis: 'load', axisMin: 0, perPanel: 'rl' },
  { id: 'tsb', label: 'Form', field: 'tsb', color: '#6B7280', short: 'Form', decimals: 1, unit: '', axis: 'form', perPanel: 'form' },
  { id: 'focus_strain', label: 'Focus', field: 'focus_strain', color: '#458141', short: 'Focus', decimals: 1, unit: 'min', axis: 'focus', axisMin: 0, dash: 4 },
  { id: 'sp', label: 'Specificity', field: 'sp', color: '#E08600', short: 'Spec', decimals: 0, unit: '%', max: 100, axis: 'spec', axisMin: 0, dash: 4 },
];

/** What the shared chips offer, and which are showing when the charts open. */
export const SIGNATURE_TOGGLES: { id: string; label: string; color: string; on: boolean }[] = [
  { id: 'ltp', label: 'Lower Threshold', color: '#6D9ED7', on: true },
  { id: 'ctl', label: 'Training Load', color: '#111827', on: true },
  { id: 'atl', label: 'Recovery Load', color: '#26B897', on: false },
  { id: 'tsb', label: 'Form', color: '#6B7280', on: false },
  { id: 'focus_strain', label: 'Focus', color: '#458141', on: false },
  { id: 'sp', label: 'Specificity', color: '#E08600', on: false },
  { id: 'breakthroughs', label: 'Breakthroughs', color: BREAKTHROUGH_COLOR, on: true },
];

// -- Activities taken out of the automatic calculations, in a strip above the signature charts ----

export type MarkerKindCfg = { label: string; color: string; lane: number };

/** Each kind gets a lane of its own in the strip (top to bottom), so several on the one day don't sit on each other.
    note's lane continues the existing ~0.3 spacing above flagged; the strip's axis max is raised to match, in
    chartHelpers.ts's addMarkerLanes, rather than renumbering the three existing lanes. */
export const MARKER_KINDS: Record<'flagged' | 'signature' | 'tl' | 'note', MarkerKindCfg> = {
  note: { label: 'Note', color: '#0D9488', lane: 1.1 },
  flagged: { label: 'Flagged', color: '#DC2626', lane: 0.8 },
  signature: { label: 'Signature set', color: '#0B5DBC', lane: 0.5 },
  tl: { label: 'TL/RL set', color: '#D97706', lane: 0.2 },
};
export const MARKER_KIND_ORDER = ['note', 'flagged', 'signature', 'tl'] as const;
